import React, { useMemo, useCallback, useState } from "react";
import MenuFilter from "./MenuFilter";
import {
  FILTER_TYPE_CHECKBOX,
  emptyArray,
  FILTER_TYPE_DATE_PRESETS,
  FILTER_TYPE_MULTI_RANGE,
} from "../../utils/constants/constants";
import { useSelector } from "react-redux";
import useMemoDeepCompare from "../../utils/useMemoDeep";
import { buckets, TYPE_GROUPS } from "../../utils/constants/dataTypes";

export default function SingleDataSourceMenuFiltersItem({
  item,
  queryUuid,
  additionalTheme,
  dataSourceAccessConfig,
  select,
  loadSingleFieldByName,
  selectedValuesOfFields,
}) {
  const selectedValues = useSelector(
    (state) =>
      state.layout.singleQueryFiltersByQueryUuid[queryUuid]?.fieldsByName[
        item.fieldName
      ]?.selectedValues ?? emptyArray
  );
  const typeGroup = buckets[item.fieldType];
  const isDateLike = typeGroup === TYPE_GROUPS.date;
  const isNumberLike = typeGroup === TYPE_GROUPS.number;
  const [loading, setLoading] = useState(false);

  const otherFieldValues = useMemoDeepCompare(
    useMemo(() => {
      return isNumberLike
        ? emptyArray
        : selectedValuesOfFields.filter(
            (selectedValuesOfField) =>
              selectedValuesOfField.key !== item.fieldName
          );
    }, [isNumberLike, item.fieldName, selectedValuesOfFields])
  );

  const loadValues = useMemo(() => {
    if (item.areValuesFixed) {
      return null;
    }

    return !isDateLike
      ? async (search, page) => {
          try {
            setLoading(true);
            await loadSingleFieldByName(
              item.fieldName,
              item.alias,
              otherFieldValues,
              search,
              page
            );
          } finally {
            setLoading(false);
          }
        }
      : null;
  }, [
    isDateLike,
    item.alias,
    item.areValuesFixed,
    item.fieldName,
    loadSingleFieldByName,
    otherFieldValues,
  ]);

  const filterType = useMemo(() => {
    function isChecked(value) {
      const charComparison = selectedValues.includes(value);
      const numberComparison = selectedValues.some(
        (selectedValue) => Number(value) === Number(selectedValue)
      );

      return numberComparison || charComparison;
    }

    const values =
      item.values?.map((value) => ({
        key: item.fieldName,
        type: item.fieldName,
        value,
        label: value,
        checked: isChecked(value),
      })) ?? (isDateLike ? [{ key: item.fieldName }] : null);

    return {
      type: isDateLike
        ? FILTER_TYPE_DATE_PRESETS
        : isNumberLike
        ? FILTER_TYPE_MULTI_RANGE
        : FILTER_TYPE_CHECKBOX,
      name: item.fieldName,
      displayName: item.fieldDisplayName,
      totalCount: item.totalCount ?? 0,
      originalTotalCount: item.originalTotalCount,
      perPage: item.perPage,
      values,
      loading,
      showEmptyValues: true,
    };
  }, [
    isDateLike,
    isNumberLike,
    item.fieldDisplayName,
    item.fieldName,
    item.perPage,
    item.originalTotalCount,
    item.totalCount,
    item.values,
    loading,
    selectedValues,
  ]);

  // We wrap the select function to ensure that the
  // singleQueryFiltersUuid option is forwarded.
  const selectSingleQuery = useCallback(
    (filter, type, shouldReloadCharts, options = {}) => {
      return select(filter, type, shouldReloadCharts, {
        ...options,
        singleQueryFiltersUuid: queryUuid,
        alias: item.alias,
      });
    },
    [item.alias, queryUuid, select]
  );

  return (
    <MenuFilter
      filterType={filterType}
      additionalTheme={additionalTheme}
      dataSourceAccessConfig={dataSourceAccessConfig}
      select={selectSingleQuery}
      loadValues={loadValues}
      selectedValues={selectedValues}
      searchFilterLoading={item.loading}
      error={item.error}
      isSingleQueryFilter
    />
  );
}
